import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';

const TYPES_ANOMALIE = [
  { value: 'absence', label: 'Absence' },
  { value: 'tenue', label: 'Tenue non conforme' },
  { value: 'sommeil', label: 'Sommeil en poste' },
  { value: 'poste_abandonne', label: 'Poste abandonne' },
  { value: 'materiel_manquant', label: 'Materiel manquant' },
  { value: 'registre', label: 'Registre non tenu' },
  { value: 'consigne_non_suivie', label: 'Consigne non suivie' },
  { value: 'autre', label: 'Autre' },
];

const GRAVITE_COLORS = {
  mineure: { bg: 'rgba(158,158,158,0.1)', color: '#9e9e9e' },
  majeure: { bg: 'rgba(245,124,0,0.1)', color: '#f57c00' },
  critique: { bg: 'rgba(229,57,53,0.1)', color: '#e53935' },
};

const STATUT_COLORS = {
  signale: '#f57c00', en_cours: '#1565c0', traite: '#2e7d32', classe: '#9e9e9e',
};

const TABS = [
  { id: 'tournee', label: 'Tournee du jour' },
  { id: 'fiches', label: 'Fiches de controle' },
  { id: 'anomalies', label: 'Anomalies' },
  { id: 'verification', label: 'Verification rapports' },
  { id: 'remplacement', label: 'Remplacement' },
  { id: 'historique', label: 'Historique' },
];

const CHECK_ITEMS = [
  { key: 'uniforme_ok', label: 'Uniforme complet et propre' },
  { key: 'equipement_ok', label: '\u00C9quipement pr\u00E9sent (radio, lampe, badge)' },
  { key: 'registre_ok', label: 'Registre de poste rempli correctement' },
  { key: 'horaires_ok', label: 'Horaires de service respect\u00E9s' },
  { key: 'consignes_generales_ok', label: 'Consignes g\u00E9n\u00E9rales respect\u00E9es' },
  { key: 'consignes_site_ok', label: 'Consignes particuli\u00E8res du site respect\u00E9es' },
];

const TYPES_MANQUEMENT = [
  { value: 'tenue_non_conforme', label: 'Tenue non conforme' },
  { value: 'absence_poste', label: 'Absence au poste' },
  { value: 'retard', label: 'Retard / horaires non respect\u00E9s' },
  { value: 'equipement_manquant', label: '\u00C9quipement manquant' },
  { value: 'registre_non_tenu', label: 'Registre non tenu' },
  { value: 'consigne_non_respectee', label: 'Consigne non respect\u00E9e' },
  { value: 'comportement', label: 'Comportement inappropri\u00E9' },
  { value: 'sommeil', label: 'Sommeil en poste' },
  { value: 'autre', label: 'Autre' },
];

export default function Controleur() {
  const [tab, setTab] = useState('tournee');
  const [tournee, setTournee] = useState(null);
  const [stats, setStats] = useState(null);
  const [fiches, setFiches] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tournees, setTournees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showFicheForm, setShowFicheForm] = useState(false);
  const [ficheForm, setFicheForm] = useState({
    site_id: '', agent_id: '', heure_visite: '',
    uniforme_ok: false, equipement_ok: false, registre_ok: false,
    horaires_ok: false, consignes_generales_ok: false, consignes_site_ok: false,
    observations: '', note: 3, signature_agent: '', signature_controleur: '',
    transmettre_rh: false, type_manquement: '', detail_manquement: '',
  });
  const [showAnomalieForm, setShowAnomalieForm] = useState(false);
  const [anomalieForm, setAnomalieForm] = useState({
    site_id: '', agent_id: '', type: 'absence', gravite: 'mineure', description: '', mesure_prise: '',
  });
  const [showRemplacementForm, setShowRemplacementForm] = useState(false);
  const [remplacementForm, setRemplacementForm] = useState({
    site_id: '', agent_absent_id: '', agent_remplacant_id: '', motif: '',
  });
  const [agentsDispo, setAgentsDispo] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNewTournee, setShowNewTournee] = useState(false);
  const [selectedSites, setSelectedSites] = useState([]);
  const [ficheDetail, setFicheDetail] = useState(null);

  // Verification (superviseur)
  const [verifStats, setVerifStats] = useState(null);
  const [fichesSoumises, setFichesSoumises] = useState([]);
  const [anomaliesNonConf, setAnomaliesNonConf] = useState([]);
  const [showRejetModal, setShowRejetModal] = useState(null);
  const [motifRejet, setMotifRejet] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ confirmee: true, commentaire_superviseur: '', transmettre_rh: true });

  const loadAll = useCallback(async () => {
    try {
      const [t, s, st, f, an, ag] = await Promise.all([
        api.get('/controleur/tournees/jour'),
        api.get('/sites'),
        api.get('/controleur/stats'),
        api.get('/controleur/fiches'),
        api.get('/controleur/anomalies'),
        api.get('/agents'),
      ]);
      setTournee(t.data);
      setSites(s.data);
      setStats(st.data);
      setFiches(f.data);
      setAnomalies(an.data);
      setAgents(ag.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadVerification = async () => {
    try {
      const [vs, fs, an] = await Promise.all([
        api.get('/controleur/stats/superviseur'),
        api.get('/controleur/fiches?statut=soumis'),
        api.get('/controleur/anomalies?statut_traitement=signale'),
      ]);
      setVerifStats(vs.data);
      setFichesSoumises(fs.data);
      setAnomaliesNonConf(an.data);
    } catch {}
  };

  useEffect(() => { if (tab === 'verification') loadVerification(); }, [tab]);

  const handleValiderFiche = async (id) => {
    try {
      await api.put(`/controleur/fiches/${id}/valider`, { statut: 'valide' });
      loadVerification();
      loadAll();
    } catch {}
  };

  const handleRejeterFiche = async () => {
    if (!showRejetModal) return;
    try {
      await api.put(`/controleur/fiches/${showRejetModal}/valider`, { statut: 'rejete', motif_rejet: motifRejet });
      setShowRejetModal(null);
      setMotifRejet('');
      loadVerification();
      loadAll();
    } catch {}
  };

  const handleConfirmerAnomalie = async () => {
    if (!showConfirmModal) return;
    try {
      await api.put(`/controleur/anomalies/${showConfirmModal}/confirmer`, confirmForm);
      setShowConfirmModal(null);
      setConfirmForm({ confirmee: true, commentaire_superviseur: '', transmettre_rh: true });
      loadVerification();
      loadAll();
    } catch {}
  };

  const loadTournees = async () => {
    try {
      const res = await api.get('/controleur/tournees');
      setTournees(res.data);
    } catch {}
  };

  useEffect(() => { if (tab === 'historique') loadTournees(); }, [tab]);

  const creerTournee = async () => {
    setSaving(true);
    try {
      await api.post('/controleur/tournees', { sites_programmes: selectedSites });
      setShowNewTournee(false);
      setSelectedSites([]);
      loadAll();
    } catch {} finally { setSaving(false); }
  };

  const cloturerTournee = async () => {
    if (!tournee || !window.confirm('Cloturer cette tournee ?')) return;
    try {
      await api.put(`/controleur/tournees/${tournee.id}/cloturer`, {});
      loadAll();
    } catch {}
  };

  const submitFiche = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/controleur/fiches', { ...ficheForm, tournee_id: tournee?.id });
      setShowFicheForm(false);
      setFicheForm({ site_id: '', agent_id: '', heure_visite: '', uniforme_ok: false, equipement_ok: false, registre_ok: false, horaires_ok: false, consignes_generales_ok: false, consignes_site_ok: false, observations: '', note: 3, signature_agent: '', signature_controleur: '', transmettre_rh: false, type_manquement: '', detail_manquement: '' });
      loadAll();
    } catch {} finally { setSaving(false); }
  };

  const submitAnomalie = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/controleur/anomalies', { ...anomalieForm, tournee_id: tournee?.id });
      setShowAnomalieForm(false);
      setAnomalieForm({ site_id: '', agent_id: '', type: 'absence', gravite: 'mineure', description: '', mesure_prise: '' });
      loadAll();
    } catch {} finally { setSaving(false); }
  };

  const loadAgentsDispo = async (site_id) => {
    try {
      const res = await api.get(`/controleur/remplacements/agents-disponibles?site_id=${site_id}`);
      setAgentsDispo(res.data);
    } catch {}
  };

  const submitRemplacement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/controleur/remplacements', remplacementForm);
      setShowRemplacementForm(false);
      setRemplacementForm({ site_id: '', agent_absent_id: '', agent_remplacant_id: '', motif: '' });
      loadAll();
    } catch {} finally { setSaving(false); }
  };

  const telechargerRapport = async (id) => {
    try {
      const res = await api.get(`/controleur/tournees/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {}
  };

  const toggleSite = (id) => {
    setSelectedSites(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  if (loading) return <div className="skeleton-card"><div className="skeleton-row" /><div className="skeleton-row" /><div className="skeleton-row" /></div>;

  return (
    <div>
{/* Stats semaine */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-label">Controles (7j)</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg></div></div>
            <div className="stat-card-value">{stats.semaine?.controles || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-label">Note moyenne</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div></div>
            <div className="stat-card-value">{stats.semaine?.note_moyenne || 0}<span style={{ fontSize: 16, fontWeight: 400 }}>/5</span></div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-label">Anomalies (7j)</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div></div>
            <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{stats.semaine?.anomalies || 0}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header"><span className="stat-card-label">Alertes en cours</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg></div></div>
            <div className="stat-card-value" style={{ color: 'var(--gss-danger)' }}>{stats.alertes_en_cours || 0}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.id} className={`gss-btn ${tab === t.id ? 'gss-btn-primary' : ''}`}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ── TOURNEE DU JOUR ── */}
      {tab === 'tournee' && (
        <div className="content-card">
          {tournee ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 600 }}>Tournee du {new Date(tournee.date).toLocaleDateString('fr-FR')}</h2>
                  <span className="gss-badge" style={{ background: tournee.statut === 'terminee' ? 'rgba(46,125,50,0.1)' : 'rgba(21,101,192,0.1)', color: tournee.statut === 'terminee' ? '#2e7d32' : '#1565c0' }}>{tournee.statut}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {tournee.statut === 'en_cours' && (
                    <>
                      <button className="gss-btn gss-btn-primary" onClick={() => setShowFicheForm(true)}>+ Fiche controle</button>
                      <button className="gss-btn" style={{ color: 'var(--gss-warning)' }} onClick={() => setShowAnomalieForm(true)}>Signaler anomalie</button>
                      <button className="gss-btn gss-btn-secondary" onClick={cloturerTournee}>Cloturer</button>
                    </>
                  )}
                  {tournee.statut === 'terminee' && (
                    <button className="gss-btn" onClick={() => telechargerRapport(tournee.id)}>Rapport PDF</button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--gss-bg)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Sites programmes</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--gss-red)' }}>{tournee.sites_programmes?.length || 0}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--gss-bg)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Controles effectues</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--gss-success)' }}>{tournee.nb_fiches || 0}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--gss-bg)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>Anomalies</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--gss-warning)' }}>{tournee.nb_anomalies || 0}</div>
                </div>
              </div>

              {/* Sites list */}
              {tournee.sites_details?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.75rem' }}>Sites a controler</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                    {tournee.sites_details.map(s => (
                      <div key={s.id} style={{
                        padding: '1rem', borderRadius: 8, border: '1px solid var(--gss-border)',
                        background: s.controle ? 'rgba(46,125,50,0.04)' : 'var(--gss-white)',
                        borderColor: s.controle ? 'rgba(46,125,50,0.2)' : 'var(--gss-border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{s.nom}</div>
                            <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)' }}>{s.adresse}, {s.ville}</div>
                          </div>
                          {s.controle ? (
                            <span className="gss-badge" style={{ background: 'rgba(46,125,50,0.1)', color: '#2e7d32' }}>Controle</span>
                          ) : (
                            <span className="gss-badge" style={{ background: 'rgba(245,124,0,0.1)', color: '#f57c00' }}>En attente</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: 'var(--gss-text-secondary)', marginBottom: '1rem' }}>Aucune tournee en cours aujourd'hui</p>
              <button className="gss-btn gss-btn-primary" onClick={() => setShowNewTournee(true)}>Demarrer une tournee</button>
            </div>
          )}
        </div>
      )}

      {/* ── FICHES DE CONTROLE ── */}
      {tab === 'fiches' && (
        <div className="content-card">
          <div className="gss-toolbar">
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Fiches de controle recentes</h3>
            <button className="gss-btn gss-btn-primary" onClick={() => setShowFicheForm(true)}>+ Nouvelle fiche</button>
          </div>

          {fiches.length === 0 ? (
            <div className="empty-state"><div className="empty-state-title">Aucune fiche</div></div>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Date</th><th>Site</th><th>Agent</th><th>Note</th><th>Checks</th><th>Statut</th></tr></thead>
                <tbody>
                  {fiches.map(f => {
                    const checks = [f.uniforme_ok, f.equipement_ok, f.registre_ok, f.horaires_ok, f.consignes_generales_ok, f.consignes_site_ok].filter(Boolean).length;
                    return (
                      <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => setFicheDetail(f)}>
                        <td style={{ color: 'var(--gss-text-secondary)' }}>{new Date(f.date).toLocaleDateString('fr-FR')}</td>
                        <td style={{ fontWeight: 600 }}>{f.site_nom}</td>
                        <td>{f.agent_prenom} {f.agent_nom}</td>
                        <td><span style={{ fontWeight: 700, color: f.note >= 4 ? '#2e7d32' : f.note >= 3 ? '#f57c00' : '#e53935' }}>{f.note || '-'}/5</span></td>
                        <td><span style={{ color: checks === 6 ? '#2e7d32' : '#f57c00' }}>{checks}/6</span></td>
                        <td>
                          {f.transmettre_rh && <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', marginRight: 4 }}>RH</span>}
                          <span className="gss-badge" style={{ background: f.statut === 'valide' ? 'rgba(46,125,50,0.1)' : 'rgba(21,101,192,0.1)', color: f.statut === 'valide' ? '#2e7d32' : '#1565c0' }}>{f.statut}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── ANOMALIES ── */}
      {tab === 'anomalies' && (
        <div className="content-card">
          <div className="gss-toolbar">
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Anomalies signalees</h3>
            <button className="gss-btn" style={{ color: 'var(--gss-warning)', borderColor: 'var(--gss-warning)' }} onClick={() => setShowAnomalieForm(true)}>Signaler anomalie</button>
          </div>

          {anomalies.length === 0 ? (
            <div className="empty-state"><div className="empty-state-title">Aucune anomalie</div></div>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Date</th><th>Type</th><th>Site</th><th>Gravite</th><th>Statut</th><th>Mesure</th></tr></thead>
                <tbody>
                  {anomalies.map(a => (
                    <tr key={a.id}>
                      <td style={{ color: 'var(--gss-text-secondary)' }}>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
                      <td style={{ fontWeight: 500 }}>{TYPES_ANOMALIE.find(t => t.value === a.type)?.label || a.type}</td>
                      <td>{a.site_nom || '-'}</td>
                      <td><span className="gss-badge" style={{ background: GRAVITE_COLORS[a.gravite]?.bg, color: GRAVITE_COLORS[a.gravite]?.color }}>{a.gravite}</span></td>
                      <td><span style={{ color: STATUT_COLORS[a.statut_traitement] }}>{a.statut_traitement}</span></td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.mesure_prise || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── VERIFICATION RAPPORTS (Superviseur) ── */}
      {tab === 'verification' && (
        <div>
          {/* Stats superviseur */}
          {verifStats && (
            <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
              <div className="stat-card">
                <div className="stat-card-header"><span className="stat-card-label">Fiches en attente</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></div></div>
                <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{verifStats.fiches_en_attente}</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header"><span className="stat-card-label">Sites couverts</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg></div></div>
                <div className="stat-card-value">{verifStats.sites?.sites || 0}<span style={{ fontSize: 14, fontWeight: 400 }}>/{verifStats.sites?.total_sites || 0}</span></div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header"><span className="stat-card-label">Anomalies (30j)</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></div></div>
                <div className="stat-card-value" style={{ color: 'var(--gss-danger)' }}>{verifStats.anomalies?.total || 0}</div>
                <div className="stat-card-sub">{verifStats.anomalies?.critiques || 0} critiques, {verifStats.anomalies?.transmis_rh || 0} transmis RH</div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header"><span className="stat-card-label">Anomalies non traitees</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
                <div className="stat-card-value" style={{ color: 'var(--gss-warning)' }}>{verifStats.anomalies?.en_attente || 0}</div>
              </div>
            </div>
          )}

          {/* Stats par controleur */}
          {verifStats?.par_controleur?.length > 0 && (
            <div className="content-card" style={{ marginBottom: '1rem' }}>
              <div className="content-card-title">Rondes par controleur (30 derniers jours)</div>
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Controleur</th><th>Tournees</th><th>Fiches</th><th>Note moy.</th></tr></thead>
                  <tbody>
                    {verifStats.par_controleur.map(c => (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.prenom} {c.nom}</td>
                        <td>{c.nb_tournees}</td>
                        <td>{c.nb_fiches}</td>
                        <td><span style={{ fontWeight: 700, color: parseFloat(c.note_moyenne) >= 4 ? '#2e7d32' : parseFloat(c.note_moyenne) >= 3 ? '#f57c00' : '#e53935' }}>{c.note_moyenne}/5</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Fiches soumises a valider */}
          <div className="content-card" style={{ marginBottom: '1rem' }}>
            <div className="content-card-title">Fiches soumises &mdash; en attente de validation</div>
            {fichesSoumises.length === 0 ? (
              <div className="empty-state" style={{ minHeight: 120 }}><div className="empty-state-title">Aucune fiche en attente</div></div>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Date</th><th>Controleur</th><th>Site</th><th>Agent</th><th>Note</th><th>Checks</th><th>RH</th><th>Actions</th></tr></thead>
                  <tbody>
                    {fichesSoumises.map(f => {
                      const checks = [f.uniforme_ok, f.equipement_ok, f.registre_ok, f.horaires_ok, f.consignes_generales_ok, f.consignes_site_ok].filter(Boolean).length;
                      return (
                        <tr key={f.id}>
                          <td style={{ color: 'var(--gss-text-secondary)' }}>{new Date(f.date).toLocaleDateString('fr-FR')}</td>
                          <td>{f.controleur_prenom} {f.controleur_nom}</td>
                          <td style={{ fontWeight: 600 }}>{f.site_nom}</td>
                          <td>{f.agent_prenom} {f.agent_nom}</td>
                          <td><span style={{ fontWeight: 700, color: f.note >= 4 ? '#2e7d32' : f.note >= 3 ? '#f57c00' : '#e53935' }}>{f.note || '-'}/5</span></td>
                          <td><span style={{ color: checks === 6 ? '#2e7d32' : '#f57c00' }}>{checks}/6</span></td>
                          <td>{f.transmettre_rh ? <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626' }}>RH</span> : '-'}</td>
                          <td>
                            <div className="gss-actions">
                              <button className="gss-btn-icon" title="Voir" onClick={() => setFicheDetail(f)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              </button>
                              <button className="gss-btn-icon" title="Valider" style={{ color: '#2e7d32', borderColor: '#2e7d32' }} onClick={() => handleValiderFiche(f.id)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="20,6 9,17 4,12"/></svg>
                              </button>
                              <button className="gss-btn-icon gss-btn-icon--delete" title="Rejeter" onClick={() => setShowRejetModal(f.id)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Anomalies non confirmees */}
          <div className="content-card">
            <div className="content-card-title">Anomalies signalees &mdash; en attente de confirmation</div>
            {anomaliesNonConf.length === 0 ? (
              <div className="empty-state" style={{ minHeight: 120 }}><div className="empty-state-title">Aucune anomalie en attente</div></div>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th>Date</th><th>Type</th><th>Site</th><th>Gravite</th><th>Controleur</th><th>Actions</th></tr></thead>
                  <tbody>
                    {anomaliesNonConf.map(a => (
                      <tr key={a.id}>
                        <td style={{ color: 'var(--gss-text-secondary)' }}>{new Date(a.created_at).toLocaleDateString('fr-FR')}</td>
                        <td style={{ fontWeight: 500 }}>{TYPES_ANOMALIE.find(t => t.value === a.type)?.label || a.type}</td>
                        <td>{a.site_nom || '-'}</td>
                        <td><span className="gss-badge" style={{ background: GRAVITE_COLORS[a.gravite]?.bg, color: GRAVITE_COLORS[a.gravite]?.color }}>{a.gravite}</span></td>
                        <td>{a.controleur_prenom} {a.controleur_nom}</td>
                        <td>
                          <button className="gss-btn-sm" style={{ background: 'rgba(46,125,50,0.06)', color: '#2e7d32', borderColor: '#2e7d32' }} onClick={() => { setShowConfirmModal(a.id); setConfirmForm({ confirmee: true, commentaire_superviseur: '', transmettre_rh: a.gravite !== 'mineure' }); }}>
                            Confirmer / Infirmer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: Rejet fiche ── */}
      {showRejetModal && (
        <div className="gss-modal-overlay" onClick={() => setShowRejetModal(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div>
                <h2>Rejeter la fiche</h2>
                <div className="gss-modal-subtitle">Motif du rejet du rapport</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowRejetModal(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <span>MOTIF DU REJET</span>
              </div>
              <div className="gss-field">
                <label>Motif du rejet *</label>
                <textarea className="gss-input" rows="3" style={{ height: 'auto', padding: '8px 12px' }} value={motifRejet} onChange={e => setMotifRejet(e.target.value)} required placeholder="Expliquez pourquoi cette fiche est rejetee..." />
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowRejetModal(null)}>Annuler</button>
              <button className="gss-btn-deploy" style={{ background: '#DC2626', color: '#fff', borderColor: '#DC2626' }} onClick={handleRejeterFiche} disabled={!motifRejet.trim()}>Confirmer le rejet</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Confirmation anomalie ── */}
      {showConfirmModal && (
        <div className="gss-modal-overlay" onClick={() => setShowConfirmModal(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <div>
                <h2>Confirmation anomalie</h2>
                <div className="gss-modal-subtitle">Validation du rapport de tournee</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowConfirmModal(null)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <span>DECISION</span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                <button className={`gss-btn ${confirmForm.confirmee ? 'gss-btn-primary' : ''}`} onClick={() => setConfirmForm({ ...confirmForm, confirmee: true })} type="button">Confirmer</button>
                <button className={`gss-btn ${!confirmForm.confirmee ? 'gss-btn-secondary' : ''}`} onClick={() => setConfirmForm({ ...confirmForm, confirmee: false, transmettre_rh: false })} type="button">Infirmer</button>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                <span>COMMENTAIRE & TRANSMISSION</span>
              </div>
              <div className="gss-field" style={{ marginBottom: '1rem' }}>
                <label>Commentaire superviseur</label>
                <textarea className="gss-input" rows="2" style={{ height: 'auto', padding: '8px 12px' }} value={confirmForm.commentaire_superviseur} onChange={e => setConfirmForm({ ...confirmForm, commentaire_superviseur: e.target.value })} />
              </div>
              {confirmForm.confirmee && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem', borderRadius: 8, background: confirmForm.transmettre_rh ? 'rgba(220,38,38,0.04)' : 'transparent', border: '1px solid', borderColor: confirmForm.transmettre_rh ? 'rgba(220,38,38,0.2)' : 'var(--gss-border)' }}>
                  <input type="checkbox" checked={confirmForm.transmettre_rh} onChange={e => setConfirmForm({ ...confirmForm, transmettre_rh: e.target.checked })} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: confirmForm.transmettre_rh ? '#DC2626' : 'var(--gss-text)' }}>Transmettre au service RH</div>
                    <div style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>RH recevra une notification automatique</div>
                  </div>
                </label>
              )}
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowConfirmModal(null)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={handleConfirmerAnomalie}>{confirmForm.confirmee ? 'Confirmer anomalie' : 'Infirmer anomalie'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── REMPLACEMENT ── */}
      {tab === 'remplacement' && (
        <div className="content-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: '0.5rem', color: 'var(--gss-danger)' }}>POSTE VACANT</h2>
          <p style={{ color: 'var(--gss-text-secondary)', marginBottom: '1.5rem' }}>Demander un remplacement d'urgence</p>
          <button className="gss-btn gss-btn-primary" style={{ fontSize: 15, padding: '0 2rem' }} onClick={() => setShowRemplacementForm(true)}>Demander remplacement</button>
        </div>
      )}

      {/* ── HISTORIQUE TOURNEES ── */}
      {tab === 'historique' && (
        <div className="content-card">
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '1rem' }}>Historique des tournees</h3>
          {tournees.length === 0 ? (
            <div className="empty-state"><div className="empty-state-title">Aucune tournee</div></div>
          ) : (
            <div className="gss-table-wrap">
              <table className="gss-table">
                <thead><tr><th>Date</th><th>Controleur</th><th>Sites</th><th>Anomalies</th><th>Note moy.</th><th>Statut</th><th>Actions</th></tr></thead>
                <tbody>
                  {tournees.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 600 }}>{new Date(t.date).toLocaleDateString('fr-FR')}</td>
                      <td>{t.controleur_prenom} {t.controleur_nom}</td>
                      <td>{t.sites_visites?.length || 0} / {t.sites_programmes?.length || 0}</td>
                      <td style={{ color: t.nb_anomalies > 0 ? 'var(--gss-warning)' : 'var(--gss-text-secondary)' }}>{t.nb_anomalies || 0}</td>
                      <td>{t.note_moyenne || '-'}/5</td>
                      <td><span className="gss-badge" style={{ background: t.statut === 'terminee' ? 'rgba(46,125,50,0.1)' : 'rgba(21,101,192,0.1)', color: t.statut === 'terminee' ? '#2e7d32' : '#1565c0' }}>{t.statut}</span></td>
                      <td>
                        {t.statut === 'terminee' && (
                          <button className="gss-btn-icon" onClick={() => telechargerRapport(t.id)} title="PDF">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/></svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL: Nouvelle Tournee ── */}
      {showNewTournee && (
        <div className="gss-modal-overlay" onClick={() => setShowNewTournee(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div>
                <h2>Nouvelle tournee</h2>
                <div className="gss-modal-subtitle">Planification de tournee de controle</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowNewTournee(false)}>&times;</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                <span>SITES A CONTROLER</span>
              </div>
              <p style={{ marginBottom: '1rem', color: 'var(--gss-text-secondary)', fontSize: 13 }}>Selectionnez les sites a controler :</p>
              <div style={{ maxHeight: 300, overflow: 'auto' }}>
                {sites.filter(s => s.actif).map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem', cursor: 'pointer', borderRadius: 6, transition: 'background 0.1s' }}>
                    <input type="checkbox" checked={selectedSites.includes(s.id)} onChange={() => toggleSite(s.id)} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{s.nom}</div>
                      <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)' }}>{s.adresse}, {s.ville}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="gss-modal-actions">
              <button className="gss-btn-cancel" onClick={() => setShowNewTournee(false)}>Annuler</button>
              <button className="gss-btn-deploy" onClick={creerTournee} disabled={saving || selectedSites.length === 0}>
                {saving ? 'Creation...' : `Demarrer (${selectedSites.length} sites)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Fiche de controle ── */}
      {showFicheForm && (
        <div className="gss-modal-overlay" onClick={() => setShowFicheForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
              <div>
                <h2>Fiche de controle</h2>
                <div className="gss-modal-subtitle">Evaluation de l'agent sur site</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowFicheForm(false)}>&times;</button>
            </div>
            <form onSubmit={submitFiche}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>IDENTIFICATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Site visit&eacute; *</label>
                    <select className="gss-input" value={ficheForm.site_id} onChange={e => setFicheForm({ ...ficheForm, site_id: e.target.value })} required>
                      <option value="">S&eacute;lectionner...</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Heure de visite</label>
                    <input type="time" className="gss-input" value={ficheForm.heure_visite} onChange={e => setFicheForm({ ...ficheForm, heure_visite: e.target.value })} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Agent contr&ocirc;l&eacute;</label>
                    <select className="gss-input" value={ficheForm.agent_id} onChange={e => setFicheForm({ ...ficheForm, agent_id: e.target.value })}>
                      <option value="">S&eacute;lectionner...</option>
                      {agents.filter(a => a.statut === 'actif').map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom} ({a.matricule})</option>)}
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>CHECKLIST CONTROLE QUALITE</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  {CHECK_ITEMS.map(c => (
                    <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid', borderColor: ficheForm[c.key] ? 'rgba(46,125,50,0.3)' : 'var(--gss-border)', cursor: 'pointer', background: ficheForm[c.key] ? 'rgba(46,125,50,0.04)' : 'transparent', transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={ficheForm[c.key]} onChange={e => setFicheForm({ ...ficheForm, [c.key]: e.target.checked })} />
                      <span style={{ fontSize: 13, color: ficheForm[c.key] ? '#2e7d32' : 'var(--gss-text)' }}>{c.label}</span>
                    </label>
                  ))}
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  <span>EVALUATION</span>
                </div>
                <div className="gss-form-grid" style={{ marginTop: '0' }}>
                  <div className="gss-field">
                    <label>Note (1-5) *</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button"
                          style={{
                            width: 40, height: 40, borderRadius: 8, border: '2px solid',
                            borderColor: ficheForm.note === n ? '#8B1A1A' : 'var(--gss-border)',
                            background: ficheForm.note === n ? '#8B1A1A' : 'transparent',
                            color: ficheForm.note === n ? '#fff' : 'var(--gss-text)',
                            fontWeight: 700, fontSize: 16, cursor: 'pointer',
                          }}
                          onClick={() => setFicheForm({ ...ficheForm, note: n })}>{n}</button>
                      ))}
                    </div>
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Observations</label>
                    <textarea className="gss-input" rows="3" style={{ height: 'auto', padding: '8px 12px' }} value={ficheForm.observations} onChange={e => setFicheForm({ ...ficheForm, observations: e.target.value })} />
                  </div>
                </div>

                {/* Section Manquement → RH */}
                <div style={{ marginTop: '1.5rem', padding: '1rem', borderRadius: 10, border: '1px solid', borderColor: ficheForm.transmettre_rh ? 'rgba(220,38,38,0.3)' : 'var(--gss-border)', background: ficheForm.transmettre_rh ? 'rgba(220,38,38,0.02)' : 'transparent', transition: 'all 0.2s' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer', marginBottom: ficheForm.transmettre_rh ? '1rem' : 0 }}>
                    <input type="checkbox" checked={ficheForm.transmettre_rh} onChange={e => setFicheForm({ ...ficheForm, transmettre_rh: e.target.checked })} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: ficheForm.transmettre_rh ? '#DC2626' : 'var(--gss-text)' }}>Transmettre &agrave; RH</div>
                      <div style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>Manquement constat&eacute; &mdash; RH sera notifi&eacute; automatiquement</div>
                    </div>
                  </label>
                  {ficheForm.transmettre_rh && (
                    <div className="gss-form-grid">
                      <div className="gss-field">
                        <label>Type de manquement *</label>
                        <select className="gss-input" value={ficheForm.type_manquement} onChange={e => setFicheForm({ ...ficheForm, type_manquement: e.target.value })} required>
                          <option value="">S&eacute;lectionner...</option>
                          {TYPES_MANQUEMENT.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                      <div className="gss-field gss-field-full">
                        <label>D&eacute;tail du manquement *</label>
                        <textarea className="gss-input" rows="2" style={{ height: 'auto', padding: '8px 12px' }} value={ficheForm.detail_manquement} onChange={e => setFicheForm({ ...ficheForm, detail_manquement: e.target.value })} required placeholder="D&eacute;crivez pr&eacute;cis&eacute;ment le manquement constat&eacute;..." />
                      </div>
                      <div className="gss-alert gss-alert-error gss-field-full" style={{ margin: 0 }}>
                        Une notification sera envoy&eacute;e automatiquement au service RH et &agrave; l'assistante de direction.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowFicheForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Enregistrement...' : 'Soumettre la fiche'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: Anomalie ── */}
      {showAnomalieForm && (
        <div className="gss-modal-overlay" onClick={() => setShowAnomalieForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div>
                <h2>Signaler une anomalie</h2>
                <div className="gss-modal-subtitle">Declaration d'anomalie constatee</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowAnomalieForm(false)}>&times;</button>
            </div>
            <form onSubmit={submitAnomalie}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>CLASSIFICATION</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Type *</label>
                    <select className="gss-input" value={anomalieForm.type} onChange={e => setAnomalieForm({ ...anomalieForm, type: e.target.value })} required>
                      {TYPES_ANOMALIE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Gravite *</label>
                    <select className="gss-input" value={anomalieForm.gravite} onChange={e => setAnomalieForm({ ...anomalieForm, gravite: e.target.value })} required>
                      <option value="mineure">Mineure</option>
                      <option value="majeure">Majeure</option>
                      <option value="critique">Critique</option>
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Site</label>
                    <select className="gss-input" value={anomalieForm.site_id} onChange={e => setAnomalieForm({ ...anomalieForm, site_id: e.target.value })}>
                      <option value="">Selectionner...</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Agent concerne</label>
                    <select className="gss-input" value={anomalieForm.agent_id} onChange={e => setAnomalieForm({ ...anomalieForm, agent_id: e.target.value })}>
                      <option value="">Selectionner...</option>
                      {agents.filter(a => a.statut === 'actif').map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>DETAILS</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Description *</label>
                    <textarea className="gss-input" rows="3" style={{ height: 'auto', padding: '8px 12px' }} value={anomalieForm.description} onChange={e => setAnomalieForm({ ...anomalieForm, description: e.target.value })} required />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Mesure prise</label>
                    <textarea className="gss-input" rows="2" style={{ height: 'auto', padding: '8px 12px' }} value={anomalieForm.mesure_prise} onChange={e => setAnomalieForm({ ...anomalieForm, mesure_prise: e.target.value })} />
                  </div>
                </div>
                {anomalieForm.gravite === 'critique' && (
                  <div className="gss-alert gss-alert-error" style={{ marginTop: '1rem' }}>
                    Anomalie CRITIQUE : une alerte sera envoyee immediatement au COS et au Superviseur General.
                  </div>
                )}
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowAnomalieForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Envoi...' : 'Signaler'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: Remplacement ── */}
      {showRemplacementForm && (
        <div className="gss-modal-overlay" onClick={() => setShowRemplacementForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              <div>
                <h2>Remplacement d'urgence</h2>
                <div className="gss-modal-subtitle">Demande de remplacement d'urgence</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowRemplacementForm(false)}>&times;</button>
            </div>
            <form onSubmit={submitRemplacement}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span>POSTE VACANT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Site du poste vacant *</label>
                    <select className="gss-input" value={remplacementForm.site_id} onChange={e => {
                      setRemplacementForm({ ...remplacementForm, site_id: e.target.value });
                      if (e.target.value) loadAgentsDispo(e.target.value);
                    }} required>
                      <option value="">Selectionner...</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Agent absent</label>
                    <select className="gss-input" value={remplacementForm.agent_absent_id} onChange={e => setRemplacementForm({ ...remplacementForm, agent_absent_id: e.target.value })}>
                      <option value="">Selectionner...</option>
                      {agents.filter(a => a.statut === 'actif').map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom}</option>)}
                    </select>
                  </div>
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                  <span>REMPLACEMENT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Agent remplacant * {agentsDispo.length > 0 && `(${agentsDispo.length} disponibles)`}</label>
                    <select className="gss-input" value={remplacementForm.agent_remplacant_id} onChange={e => setRemplacementForm({ ...remplacementForm, agent_remplacant_id: e.target.value })} required>
                      <option value="">Selectionner...</option>
                      {agentsDispo.map(a => <option key={a.id} value={a.id}>{a.prenom} {a.nom} ({a.matricule}) — {a.ville}</option>)}
                    </select>
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Motif</label>
                    <input className="gss-input" value={remplacementForm.motif} onChange={e => setRemplacementForm({ ...remplacementForm, motif: e.target.value })} placeholder="Ex: absence non justifiee" />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowRemplacementForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>{saving ? 'Envoi...' : 'Demander remplacement'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL: Detail fiche ── */}
      {ficheDetail && (
        <div className="gss-modal-overlay" onClick={() => setFicheDetail(null)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              <div>
                <h2>Fiche de controle — {ficheDetail.site_nom}</h2>
                <div className="gss-modal-subtitle">Fiche de controle detaillee</div>
              </div>
              <button className="gss-modal-close" onClick={() => setFicheDetail(null)}>&times;</button>
            </div>
            <div className="gss-detail-grid">
              <div className="gss-detail-item"><label>Date</label><span>{new Date(ficheDetail.date).toLocaleDateString('fr-FR')}</span></div>
              <div className="gss-detail-item"><label>Heure visite</label><span>{ficheDetail.heure_visite || '-'}</span></div>
              <div className="gss-detail-item"><label>Agent</label><span>{ficheDetail.agent_prenom} {ficheDetail.agent_nom}</span></div>
              <div className="gss-detail-item"><label>Note</label><span style={{ fontWeight: 700, fontSize: 18 }}>{ficheDetail.note || '-'}/5</span></div>
              <div className="gss-detail-item"><label>Contr&ocirc;leur</label><span>{ficheDetail.controleur_prenom} {ficheDetail.controleur_nom}</span></div>
              <div className="gss-detail-item"><label>Transmis RH</label><span style={{ color: ficheDetail.transmettre_rh ? '#DC2626' : 'var(--gss-text-light)', fontWeight: ficheDetail.transmettre_rh ? 600 : 400 }}>{ficheDetail.transmettre_rh ? 'OUI' : 'Non'}</span></div>
            </div>
            <div style={{ padding: '0 1.5rem 1rem' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.5rem' }}>Checklist contr&ocirc;le qualit&eacute;</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {CHECK_ITEMS.map(c => (
                  <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 13, padding: '0.4rem 0.6rem', borderRadius: 6, background: ficheDetail[c.key] ? 'rgba(46,125,50,0.04)' : 'rgba(229,57,53,0.04)' }}>
                    <span style={{ color: ficheDetail[c.key] ? '#2e7d32' : '#e53935', fontWeight: 700 }}>{ficheDetail[c.key] ? '\u2713' : '\u2717'}</span>
                    <span>{c.label}</span>
                  </div>
                ))}
              </div>
              {ficheDetail.observations && (
                <div style={{ marginTop: '1rem' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: '0.25rem' }}>Observations</h3>
                  <p style={{ color: 'var(--gss-text-secondary)', fontSize: 13, whiteSpace: 'pre-wrap' }}>{ficheDetail.observations}</p>
                </div>
              )}
              {ficheDetail.transmettre_rh && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: 8, background: 'rgba(220,38,38,0.04)', border: '1px solid rgba(220,38,38,0.15)' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: '#DC2626', marginBottom: '0.25rem' }}>Manquement transmis &agrave; RH</h3>
                  <p style={{ fontSize: 13, color: 'var(--gss-text-secondary)' }}><strong>Type :</strong> {TYPES_MANQUEMENT.find(t => t.value === ficheDetail.type_manquement)?.label || ficheDetail.type_manquement || '-'}</p>
                  <p style={{ fontSize: 13, color: 'var(--gss-text-secondary)', whiteSpace: 'pre-wrap' }}><strong>D&eacute;tail :</strong> {ficheDetail.detail_manquement || '-'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
